import React, {useContext, useState} from 'react';
import {Accordion, AccordionCollapse, AccordionContext, Form, useAccordionToggle} from 'react-bootstrap';
import styled from 'styled-components';
import useBasketContext from '../../../../contexts/basket/useBasketContext';
import { CTA } from '../../../general/Button';
import {t} from "ttag";
import PageSpinnerThree from "../../../general/PageSpinnerThree";
import useCustomerContext from "../../../../contexts/customer/useCustomerContext";
import {iLog} from "../../../../index";
import {money} from "../../../../services/format";
import BasketItem from "../../Menu/components/basket/BasketItem";
import useOperatorContext from "../../../../contexts/operator/useOperatorContext";
import useSiteContext from "../../../../contexts/site/useSiteContext";
import {discountLabel} from "../../../../services/offers";


const PromoForm = () => {
    const [loading, setLoading] = useState(false);
    const [promoCode, setPromoCode] = useState('');

    const operatorContext = useOperatorContext();
    const basketContext = useBasketContext();
    const siteContext = useSiteContext();
    const customerContext = useCustomerContext();

    const addPromoCode = () => {
        setLoading(true);

        const email = basketContext.customerEmail ?? customerContext.customerFormData.email;

        iLog('customer email', [email, basketContext.customerEmail, customerContext.customerFormData.email]);

        basketContext.validatePromoCode(promoCode, email)
            .finally(() => {
                setLoading(false);
                setPromoCode('');
            });
    };

    const removePromo = async () => {
        try {
            basketContext.setPromoError('');
            await basketContext.validateBasket(true, true);
        } catch (error) {
            basketContext.setLoading(false);
        }
    };

    const renderPromoElement = () => {
        if ( !basketContext.validatedBasket ) {
            return null;
        }

        return basketContext.validatedBasket?.codes
            .map((discount) => {
                return (
                    <StyledPromoLine key={discount.type + discount.text}>
                        <div className={'promo__details'}>
                            <span className="promo__type">{discountLabel(discount, siteContext.menu, (pence: number) => money(pence, operatorContext.operator?.ccy, operatorContext.operator?.locale))}</span>
                            { discount.type !== 'pos' && !discount.offer ? (
                                <Code>
                                    <div>
                                        <span>{ discount.reference }</span>
                                        <span className="material-symbols-outlined cursor-pointer" onClick={removePromo}>close</span>
                                    </div>
                                </Code>
                            ) : null }
                        </div>
                        <span className="promo__value">{money(-discount.amount, operatorContext.operator?.ccy, operatorContext.operator?.locale)}</span>
                    </StyledPromoLine>
                );
            });
    };

    // An offer is applied by itself (and would be again if it were taken off), so it has no code to remove; a code can still take its place.
    const codes = basketContext.validatedBasket?.codes ?? [];

    if (codes.length && !codes.every((code) => code.offer)) {
        return (<>{renderPromoElement()}</>)
    }


    function CustomToggle({ children, eventKey, callback }: any) {
        const currentEventKey = useContext(AccordionContext);

        const isCurrentEventKey = currentEventKey === eventKey;

        const decoratedOnClick = useAccordionToggle(eventKey, () => {
            // clear out any errors when the accordion opens..
            if ( !isCurrentEventKey ) {
                basketContext.setPromoError('');
            }
            callback && callback(eventKey);
        });

        return (
            <button
                type="button"
                className={`your-order-toggle customToggle ${isCurrentEventKey ? 'show' : ''}`}
                onClick={decoratedOnClick}
            >
                {children}
                <span className="material-symbols-outlined">keyboard_arrow_down</span>
            </button>
        );
    }

    return (
        <>
            {renderPromoElement()}
            <StyledAccordion defaultActiveKey="-1">
                <CustomToggle eventKey="0">
                    <div className="toggle">
                        <span className="toggle__title">{t`Add discount or promo code`}</span>
                    </div>
                </CustomToggle>
                <AccordionCollapse eventKey="0" timeout={2000}>
                    <>
                        <StyledGroup controlId="formCheckoutPromo" className="promo">
                            <Form.Control
                                placeholder={t`Enter code`}
                                className=""
                                value={promoCode}
                                onChange={(e) => setPromoCode(e.target.value)}>
                            </Form.Control>

                            {promoCode.length > 0 ? (
                                <CTA
                                    className="submit-promo-btn"
                                    disabled={loading}
                                    loading={loading}
                                    onClick={() => addPromoCode()}
                                    block
                                >
                                    <PageSpinnerThree show={loading} text={t`Apply`}/>
                                </CTA>
                            ) : null }
                        </StyledGroup>

                        { basketContext.promoError !== '' && (
                            <ErrorContainer>
                                <span className="material-symbols-outlined">info</span>
                                { basketContext.promoError }
                            </ErrorContainer>
                        ) }
                    </>
                </AccordionCollapse>
            </StyledAccordion>
        </>
    );
};

const StyledAccordion = styled(Accordion)`
    padding-bottom: 12px;

    .customToggle {
        transition: background-color 0.15s ease-in-out;

        .material-symbols-outlined {
            transition: transform 0.15s ease-in-out;
            font-size: 24px;
            line-height: 24px;
        }

        &.show {
            .material-symbols-outlined {
                transform: rotate(180deg);
            }
        }
    }

    .promoSubmit {
        height: 48px;
        margin: 0;
        padding: 0 23px;
        margin-left: 16px;

        @media screen and (min-width: 768px) {
            padding: 0 40px;
            min-width: 152px;
            margin-left: 24px;
        }
    }

    > button {
        width: 100%;
        background: transparent;
        border: none;
        outline: none;
        box-shadow: none;
        display: flex;
        padding: 0;
        margin: 0;
        font-weight: 600;
        align-items: center;
        text-align: left;
        color: var(--text_body_color);

        img {
            transition: transform 0.2s linear;
        }

        &.show {
            img {
                transform: rotate(180deg);
            }
        }

        .toggle {
            width: 100%;
            display: flex;
            color: var(--text_body_color);
            flex-wrap: wrap;
            flex-grow: 1;
            > span {
                margin-right: 8px;
            }
            font-size: 16px;
            font-weight: 500;
            line-height: 24px;
        }
    }
`;


const ErrorContainer = styled.div`
    color: var(--invalid);
    padding-top: 8px;
    display: flex;
    align-items: flex-start;
    img { margin-right: 12px; }

    & .material-symbols-outlined {
        margin-right: 8px;
    }
`;

const Code = styled.div`
    display: inline-block;
    margin-top: 8px;
    div {
        font-weight: 600;
        line-height: 20px;
        border-radius: var(--radius_m);
        background-color: var(--bg-grey);
        padding: 5px 8px;
        display: flex;
        align-items: center;
        color: var(--brand);

        span {
            padding-right: 8px;
            font-size: 14px;
            line-height: 20px;
        }
        font-size: 16px;
        span { font-size: 16px; }
    }
`;

const StyledGroup = styled(Form.Group)`
    width: 100%;
    margin: 8px 0 0 0 !important;
    position: relative;

    input {
        @media screen and (min-width: 768px) {
            min-width: 220px;
        }
    }

    button {
        width: auto;
    }

    .submit-promo-btn {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        background-color: transparent;
        color: var(--text-black);
        height: auto;
        padding: 4px;

        &:disabled {
            background: var(--bg-disabled);
            color: white;
        }
    }
`;

const StyledPromoLine = styled.div`
    display: flex;
    gap: 12px;
    font-size: 16px;
    padding-bottom: 16px;
    .promo {
        &__details {
            flex-grow: 1;
        }
        &__type {
            display: block;
        }
    }
`;

export default PromoForm;
